import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Clock, Check, X, Trash2, User, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminAppointments = () => {
  const [requests, setRequests] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [counterModal, setCounterModal] = useState(null);
  const [proposedDate, setProposedDate] = useState('');
  const [proposedTime, setProposedTime] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [activeTab, setActiveTab] = useState('appointments');

  const getToken = () => localStorage.getItem('adminToken');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${getToken()}` };
      const [reqRes, aptRes] = await Promise.all([
        axios.get(`${API}/admin/appointment-requests`, { headers }),
        axios.get(`${API}/admin/appointments`, { headers }),
      ]);
      setRequests(reqRes.data);
      setAppointments(aptRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const deleteAppointment = async (id) => {
    if (!window.confirm('Delete this appointment? It will be removed from the patient\'s view immediately.')) return;
    try {
      const headers = { Authorization: `Bearer ${getToken()}` };
      await axios.delete(`${API}/admin/appointments/${id}`, { headers });
      toast.success('Appointment deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete appointment');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const headers = { Authorization: `Bearer ${getToken()}` };
      await axios.put(`${API}/admin/appointment-requests/${id}`, { status }, { headers });
      toast.success(`Request ${status} successfully`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update request');
    }
  };

  const handleCounterPropose = async (e) => {
    e.preventDefault();
    if (!proposedDate || !proposedTime) {
      toast.error('Please select a date and time');
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${getToken()}` };
      await axios.put(`${API}/admin/appointment-requests/${counterModal.id}`, {
        status: 'counter_proposed',
        proposed_date: proposedDate,
        proposed_time: proposedTime,
        admin_note: adminNote
      }, { headers });
      toast.success('Counter-proposal sent to patient');
      setCounterModal(null);
      setProposedDate('');
      setProposedTime('');
      setAdminNote('');
      fetchData();
    } catch (error) {
      toast.error('Failed to send counter-proposal');
    }
  };

  const deleteRequest = async (id) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;
    try {
      const headers = { Authorization: `Bearer ${getToken()}` };
      await axios.delete(`${API}/admin/appointment-requests/${id}`, { headers });
      toast.success('Request deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete request');
    }
  };

  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);

  const statusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800',
      approved: 'bg-emerald-100 text-emerald-800',
      denied: 'bg-red-100 text-red-800',
      counter_proposed: 'bg-blue-100 text-blue-800',
      confirmed: 'bg-emerald-100 text-emerald-800',
      rejected_by_patient: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      pending: 'Pending',
      approved: 'Approved',
      denied: 'Denied',
      counter_proposed: 'Counter-Proposed',
      confirmed: 'Confirmed',
      rejected_by_patient: 'Patient Declined'
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-800'}`} data-testid={`status-badge-${status}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F4C5C]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="admin-appointments-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#1F2937] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          Appointments
        </h1>
        <p className="text-[#6B7280]">Manage scheduled appointments and incoming requests</p>
      </div>

      {/* Main Tabs */}
      <div className="flex gap-2 mb-6 border-b border-slate-200" data-testid="main-tabs">
        <button onClick={() => setActiveTab('appointments')} className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'appointments' ? 'border-[#0F4C5C] text-[#0F4C5C]' : 'border-transparent text-[#6B7280] hover:text-[#1F2937]'}`} data-testid="tab-appointments">
          Scheduled ({appointments.length})
        </button>
        <button onClick={() => setActiveTab('requests')} className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'requests' ? 'border-[#0F4C5C] text-[#0F4C5C]' : 'border-transparent text-[#6B7280] hover:text-[#1F2937]'}`} data-testid="tab-requests">
          Requests ({requests.length})
        </button>
      </div>

      {/* Scheduled Appointments Tab */}
      {activeTab === 'appointments' && (
        <>
          {appointments.length === 0 ? (
            <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
              <Calendar className="w-12 h-12 text-[#6B7280] mx-auto mb-4" />
              <p className="text-[#6B7280] text-lg">No scheduled appointments</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="appointments-table">
                  <thead className="bg-[#F9FAFB] border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">Patient</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">Date</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">Time</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">Service</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">Doctor</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-[#1F2937]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {appointments.map((apt, idx) => {
                      const isPast = new Date(`${apt.date}T${apt.time || '00:00'}`) < new Date();
                      return (
                        <tr key={apt.id} className={`hover:bg-[#F9FAFB] transition-colors ${isPast ? 'opacity-50' : ''}`} data-testid={`appointment-row-${idx}`}>
                          <td className="px-6 py-4">
                            <div className="font-medium text-[#1F2937]">{apt.patient_name}</div>
                            <div className="text-xs text-[#6B7280]">{apt.patient_email}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-[#1F2937]">{apt.date}</td>
                          <td className="px-6 py-4 text-sm text-[#1F2937]">{apt.time}</td>
                          <td className="px-6 py-4 text-sm text-[#1F2937]">{apt.service || '-'}</td>
                          <td className="px-6 py-4 text-sm text-[#1F2937]">{apt.doctor || '-'}</td>
                          <td className="px-6 py-4 text-center">
                            <button
                              onClick={() => deleteAppointment(apt.id)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium"
                              data-testid={`delete-appointment-${idx}`}
                            >
                              <Trash2 className="w-4 h-4" /> Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Requests Tab */}
      {activeTab === 'requests' && (
        <>
      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="filter-tabs">
        {['all', 'pending', 'approved', 'counter_proposed', 'confirmed', 'denied'].map(tab => (
          <button
            key={tab}
            onClick={() => setFilter(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === tab ? 'bg-[#0F4C5C] text-white' : 'bg-white text-[#6B7280] border border-slate-200 hover:bg-slate-50'
            }`}
            data-testid={`filter-${tab}`}
          >
            {tab === 'counter_proposed' ? 'Counter-Proposed' : tab.charAt(0).toUpperCase() + tab.slice(1)} ({tab === 'all' ? requests.length : requests.filter(r => r.status === tab).length})
          </button>
        ))}
      </div>

      {/* Requests List */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center" data-testid="no-requests">
          <Calendar className="w-12 h-12 text-[#6B7280] mx-auto mb-4" />
          <p className="text-[#6B7280] text-lg">No appointment requests found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map(req => (
            <div key={req.id} className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm" data-testid={`request-card-${req.id}`}>
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-[#0F4C5C]/10 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-[#0F4C5C]" />
                    </div>
                    <div>
                      <div className="font-semibold text-[#1F2937]" data-testid="request-patient-name">{req.patient_name}</div>
                      <div className="text-sm text-[#6B7280]">{req.patient_email} | {req.patient_phone}</div>
                    </div>
                    <div className="ml-auto lg:ml-4">{statusBadge(req.status)}</div>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-[#6B7280]">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-4 h-4" />
                      <span data-testid="request-date">
                        Requested: {new Date(req.requested_date).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-4 h-4" />
                      <span data-testid="request-time">{req.requested_time}</span>
                    </div>
                    {req.notes && <div className="text-[#6B7280] italic">Note: {req.notes}</div>}
                  </div>
                  {req.proposed_date && (
                    <div className="mt-2 p-2 bg-blue-50 rounded-lg text-sm text-blue-800">
                      <strong>Proposed:</strong> {new Date(req.proposed_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })} at {req.proposed_time}
                      {req.admin_note && <span className="ml-2 italic">- {req.admin_note}</span>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {req.status === 'pending' && (
                    <>
                      <button
                        onClick={() => updateStatus(req.id, 'approved')}
                        className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium transition-colors"
                        data-testid={`approve-btn-${req.id}`}
                      >
                        <Check className="w-4 h-4" /> Approve
                      </button>
                      <button
                        onClick={() => { setCounterModal(req); setProposedDate(''); setProposedTime(''); setAdminNote(''); }}
                        className="flex items-center gap-1.5 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium transition-colors"
                        data-testid={`counter-btn-${req.id}`}
                      >
                        <MessageSquare className="w-4 h-4" /> Suggest Different Time
                      </button>
                      <button
                        onClick={() => updateStatus(req.id, 'denied')}
                        className="flex items-center gap-1.5 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm font-medium transition-colors"
                        data-testid={`deny-btn-${req.id}`}
                      >
                        <X className="w-4 h-4" /> Deny
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => deleteRequest(req.id)}
                    className="flex items-center gap-1.5 px-3 py-2 text-[#6B7280] hover:text-red-500 border border-slate-200 rounded-lg text-sm transition-colors"
                    data-testid={`delete-btn-${req.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* End Requests Tab */}
      </>
      )}

      {/* Counter-Proposal Modal */}
      {counterModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setCounterModal(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="counter-proposal-modal">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-[#1F2937]" style={{ fontFamily: 'Playfair Display, serif' }}>Suggest Different Time</h2>
              <button onClick={() => setCounterModal(null)} className="text-[#6B7280] hover:text-[#1F2937]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="bg-slate-50 rounded-lg p-3 mb-5 text-sm">
                <p className="text-[#6B7280]">Patient <strong>{counterModal.patient_name}</strong> requested:</p>
                <p className="font-medium text-[#1F2937]">
                  {new Date(counterModal.requested_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at {counterModal.requested_time}
                </p>
              </div>
              <form onSubmit={handleCounterPropose} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#1F2937] mb-2">Proposed Date</label>
                  <input
                    type="date"
                    value={proposedDate}
                    onChange={e => setProposedDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]"
                    data-testid="proposed-date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1F2937] mb-2">Proposed Time</label>
                  <input
                    type="time"
                    value={proposedTime}
                    onChange={e => setProposedTime(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]"
                    data-testid="proposed-time-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1F2937] mb-2">Note to Patient (optional)</label>
                  <textarea
                    value={adminNote}
                    onChange={e => setAdminNote(e.target.value)}
                    rows={2}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] resize-none"
                    placeholder="e.g., The requested time is unavailable, this slot works better..."
                    data-testid="admin-note-input"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-blue-600 text-white hover:bg-blue-700 rounded-full px-6 py-3 font-semibold transition-all"
                  data-testid="send-counter-proposal-btn"
                >
                  Send Counter-Proposal
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAppointments;
